import { FileText, FileCheck, FileSignature, Download, ExternalLink, ShieldCheck, FileIcon, Clock } from "lucide-react";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";

export type GeneralDocument = {
  id: string;
  title: string;
  documentType: string;
  status: string;
  signatureStatus: string | null;
  signedFileKey: string | null;
  unsignedFileKey: string | null;
  createdAt: string;
};

export type AccountingDocumentSummary = {
  id: string;
  documentNumber: string | null;
  documentType: string;
  status: string;
  totalAmount: number;
  outstandingAmount: number | null;
  issueDate: string | null;
  dueDate: string | null;
  paidAt: string | null;
  documentUrl: string | null;
  createdAt: string;
};

interface AuctionDocumentsTabProps {
  documents: AccountingDocumentSummary[];
  generalDocuments: GeneralDocument[];
  onDownloadGeneral?: (doc: GeneralDocument) => void;
}

export function AuctionDocumentsTab({ 
  documents, 
  generalDocuments,
}: AuctionDocumentsTabProps) {
  
  const formatDate = (iso: string | null) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("nb-NO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("nb-NO", {
      style: "currency",
      currency: "NOK",
    }).format(amount);
  };

  const getAccountingDocLabel = (type: string) => {
    switch (type) {
      case "invoice": return "Faktura";
      case "credit_note": return "Kreditnota";
      case "purchase_settlement": return "Kjøpsoppgjør";
      default: return type;
    }
  };

  const getSignatureStatusBadge = (status: string | null) => {
    switch (status) {
      case "signed":
        return <Badge variant="success" className="gap-1.5"><ShieldCheck className="h-3 w-3" /> Signert</Badge>;
      case "pending":
        return <Badge variant="warning" className="gap-1.5"><Clock className="h-3 w-3" /> Venter på signatur</Badge>;
      case "cancelled":
        return <Badge variant="destructive">Avbrutt</Badge>;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 mt-6">
      {/* ─── Andre dokumenter (Kontrakter etc) ─── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <FileSignature className="h-4 w-4 text-primary" />
            Dokumenter og avtaler
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-border">
            {generalDocuments.length === 0 ? (
              <div className="px-6 py-10 text-center text-muted-foreground text-sm">
                Ingen generelle dokumenter funnet.
              </div>
            ) : (
              generalDocuments.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between px-6 py-4 hover:bg-muted/30 transition-colors">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="h-10 w-10 rounded-xl bg-primary/5 flex items-center justify-center flex-shrink-0">
                      <FileIcon className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="font-medium text-sm truncate">{doc.title}</p>
                        {getSignatureStatusBadge(doc.signatureStatus)}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{doc.documentType}</span>
                        <span>·</span>
                        <span>Opprettet {formatDate(doc.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {doc.signedFileKey && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="gap-1.5"
                        onClick={() => window.open(`/api/admin/documents/${doc.id}/download?file=signed`, "_blank")}
                      >
                        <Download className="h-3.5 w-3.5" />
                        Signert
                      </Button>
                    )}
                    {doc.unsignedFileKey && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="gap-1.5"
                        onClick={() => window.open(`/api/admin/documents/${doc.id}/download?file=unsigned`, "_blank")}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        Utkast
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* ─── Regnskapsdokumenter ─── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" />
            Regnskapsdokumenter
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-border">
            {documents.length === 0 ? (
              <div className="px-6 py-10 text-center text-muted-foreground text-sm">
                Ingen regnskapsdokumenter funnet.
              </div>
            ) : (
              documents.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between px-6 py-4 hover:bg-muted/30 transition-colors">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="h-10 w-10 rounded-xl bg-primary/5 flex items-center justify-center flex-shrink-0">
                      <FileCheck className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="font-medium text-sm truncate">
                          {getAccountingDocLabel(doc.documentType)} {doc.documentNumber && `#${doc.documentNumber}`}
                        </p>
                        <Badge variant={doc.status === "paid" ? "success" : "secondary"}>
                          {doc.status === "paid" ? "Betalt" : doc.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">{formatCurrency(doc.totalAmount)}</span>
                        <span>·</span>
                        <span>Dato: {formatDate(doc.issueDate)}</span>
                        {doc.dueDate && (
                          <>
                            <span>·</span>
                            <span>Forfall: {formatDate(doc.dueDate)}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {doc.documentUrl && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-1.5"
                        onClick={() => window.open(doc.documentUrl!, "_blank", "noopener,noreferrer")}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        Vis PDF
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
